import type {
  RawFreshness,
  RawLineupResponse,
  RawPlayerDetailResponse,
  RawRosterEntry,
  RawTeamTimelineResponse,
  RawTrendsResponse,
  RosterSlot,
} from "./apiShapes";

export * from "./apiShapes";

/**
 * Type definitions for the Fantasy Stats Tracker API.
 * All field names use snake_case to match the API response format.
 */

// ============================================================================
// Auth & User
// ============================================================================

export interface User {
  id: number;
  email: string;
  created_at: string; // ISO 8601 datetime
}

// ============================================================================
// Scoring & Rules
// ============================================================================

export interface Bonus {
  stat: string;
  threshold: number;
  points: number;
}

export interface PointsAllowedTier {
  max: number;
  points: number;
}

export interface KickingRules {
  fg_made_0_19?: number;
  fg_made_20_29?: number;
  fg_made_30_39?: number;
  fg_made_40_49?: number;
  fg_made_50_59?: number;
  fg_made_60_?: number;
  fg_missed?: number;
  pat_made?: number;
  pat_missed?: number;
}

export interface DSTRules {
  def_sacks?: number;
  def_interceptions?: number;
  def_fumbles_forced?: number;
  def_tds?: number;
  def_safeties?: number;
  def_tackles_solo?: number;
  def_tackle_assists?: number;
  def_tackles_for_loss?: number;
  def_pass_defended?: number;
  points_allowed_tiers?: PointsAllowedTier[];
}

export interface ScoringRules {
  version: number;
  // Flat multipliers for offensive stats
  passing_yards?: number;
  passing_tds?: number;
  passing_interceptions?: number;
  passing_2pt_conversions?: number;
  passing_first_downs?: number;
  passing_air_yards?: number;
  passing_epa?: number;
  passing_cpoe?: number;
  sacks_suffered?: number;
  sack_fumbles_lost?: number;
  rushing_yards?: number;
  rushing_tds?: number;
  rushing_2pt_conversions?: number;
  rushing_first_downs?: number;
  rushing_epa?: number;
  rushing_fumbles_lost?: number;
  receptions?: number;
  receiving_yards?: number;
  receiving_tds?: number;
  receiving_2pt_conversions?: number;
  receiving_first_downs?: number;
  receiving_air_yards?: number;
  receiving_yards_after_catch?: number;
  receiving_epa?: number;
  receiving_fumbles_lost?: number;
  target_share?: number;
  air_yards_share?: number;
  wopr?: number;
  racr?: number;
  special_teams_tds?: number;
  punt_return_yards?: number;
  kickoff_return_yards?: number;
  fumbles_lost_total?: number;
  // Special forms
  bonuses?: Bonus[];
  kicking?: KickingRules;
  dst?: DSTRules;
}

export interface ScoringProfile {
  id: number;
  user_id: number | null; // null = system preset
  name: string;
  is_preset: boolean;
  rules: ScoringRules;
  created_at: string; // ISO 8601 datetime
}

// ============================================================================
// Players & Stats
// ============================================================================


export interface Player {
  /**
   * Present on search results (`GET /api/players`), which nest aggregated stat values
   * under `stats` rather than flattening them onto the player. Absent on bio-only
   * responses, hence optional.
   */
  stats?: Partial<PlayerWeekStats>;
  /** Points for the caller's scoring profile, computed server-side. Search results only. */
  fantasy_points?: number;
  /** The team for the queried season. Distinct from `latest_team`, which is bio-only. */
  team?: string;
  gsis_id: string;
  full_name: string;
  first_name: string;
  last_name: string;
  position: string; // QB, RB, WR, TE, K, DEF, etc.
  position_group: string;
  headshot_url?: string;
  birth_date?: string; // YYYY-MM-DD
  height?: string;
  weight?: number;
  college?: string;
  status: string; // active, injured, retired, etc.
  latest_team?: string; // NFL team abbreviation
  entry_year?: number;
  rookie_year?: number;
  draft_club?: string;
  draft_number?: number;
  espn_id?: string;
  pfr_id?: string;
  smart_id?: string;
}

export interface PlayerWeekStats {
  id: number;
  gsis_id: string;
  season: number;
  week: number;
  season_type: "REG" | "POST";
  game_id: string;
  team: string;
  opponent_team: string;
  // Passing
  completions?: number;
  attempts?: number;
  passing_yards?: number;
  passing_tds?: number;
  passing_interceptions?: number;
  passing_first_downs?: number;
  passing_air_yards?: number;
  passing_epa?: number;
  passing_cpoe?: number;
  passing_2pt_conversions?: number;
  sacks_suffered?: number;
  sack_fumbles_lost?: number;
  // Rushing
  carries?: number;
  rushing_yards?: number;
  rushing_tds?: number;
  rushing_first_downs?: number;
  rushing_epa?: number;
  rushing_2pt_conversions?: number;
  rushing_fumbles_lost?: number;
  // Receiving
  receptions?: number;
  targets?: number;
  receiving_yards?: number;
  receiving_tds?: number;
  receiving_first_downs?: number;
  receiving_air_yards?: number;
  receiving_yards_after_catch?: number;
  receiving_epa?: number;
  receiving_2pt_conversions?: number;
  receiving_fumbles_lost?: number;
  target_share?: number;
  air_yards_share?: number;
  wopr?: number;
  racr?: number;
  // Misc
  special_teams_tds?: number;
  punt_return_yards?: number;
  kickoff_return_yards?: number;
  fumbles_lost_total?: number;
  // Kicking
  fg_made?: number;
  fg_att?: number;
  fg_missed?: number;
  fg_long?: number;
  pat_made?: number;
  pat_att?: number;
  pat_missed?: number;
  fg_made_0_19?: number;
  fg_made_20_29?: number;
  fg_made_30_39?: number;
  fg_made_40_49?: number;
  fg_made_50_59?: number;
  fg_made_60_?: number;
  // IDP
  def_sacks?: number;
  def_interceptions?: number;
  def_tds?: number;
  def_fumbles_forced?: number;
  def_tackles_solo?: number;
  def_tackle_assists?: number;
  def_tackles_for_loss?: number;
  def_pass_defended?: number;
  def_safeties?: number;
  // Reference (nflverse's own totals, used for validation only)
  fantasy_points?: number;
  fantasy_points_ppr?: number;
}

export interface Injury {
  season: number;
  week: number;
  gsis_id: string;
  team: string;
  report_primary_injury: string;
  report_status: string;
  practice_status?: string;
  date_modified: string; // ISO 8601 datetime
}

export interface SnapCount {
  season: number;
  week: number;
  gsis_id: string;
  team: string;
  offense_snaps?: number;
  offense_pct?: number;
  defense_snaps?: number;
  defense_pct?: number;
  st_snaps?: number;
  st_pct?: number;
}

/** GET /api/players/:id. Verified wire shape — see ./apiShapes.ts. */
export type PlayerDetail = RawPlayerDetailResponse;

/** GET /api/players/:id/trends. Verified wire shape — see ./apiShapes.ts. */
export type PlayerTrends = RawTrendsResponse;

// ============================================================================
// Teams & Rosters
// ============================================================================

/** roster rows from GET /api/teams/:id/roster. Verified wire shape — see ./apiShapes.ts. */
export type RosterEntry = RawRosterEntry;

export interface FantasyTeam {
  id: number;
  user_id: number;
  name: string;
  season: number;
  scoring_profile_id: number;
  /** True when the team runs a second FLEX in place of its kicker slot. */
  flex_instead_of_kicker: boolean;
  /** Slot -> how many of it this team has. Omits slots the team doesn't run. */
  roster_limits: Partial<Record<RosterSlot, number>>;
  created_at: string; // ISO 8601 datetime
  roster?: RosterEntry[];
  scoring_profile?: ScoringProfile;
  week_totals?: {
    week: number;
    total_points: number;
  }[];
}

/** GET /api/teams/:id/timeline. Verified wire shape — see ./apiShapes.ts. */
export type RosterTimeline = RawTeamTimelineResponse;

/** GET /api/teams/:id/lineup. Verified wire shape — see ./apiShapes.ts. */
export type LineupOptimal = RawLineupResponse;

// ============================================================================
// Games & Schedule
// ============================================================================

export interface Game {
  game_id: string;
  season: number;
  week: number;
  game_type: string;
  gameday: string; // ISO 8601 date
  weekday: string;
  gametime?: string;
  home_team: string;
  away_team: string;
  home_score?: number;
  away_score?: number;
  stadium?: string;
  roof?: string;
  surface?: string;
}

// ============================================================================
// Meta & Health
// ============================================================================

/** GET /api/meta/freshness. Verified wire shape — see ./apiShapes.ts. */
export type Freshness = RawFreshness;

export interface HealthStatus {
  status: "healthy" | "unhealthy";
  database: boolean;
  checks?: {
    [key: string]: boolean | string;
  };
}

// ============================================================================
// Error Handling
// ============================================================================

export interface ErrorResponse {
  code: string;
  message: string;
  field?: string;
}

export class ApiError extends Error {
  status: number;
  code: string;
  field?: string;

  constructor(
    status: number,
    code: string,
    message: string,
    field?: string
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.field = field;
  }
}

// ============================================================================
// Pagination
// ============================================================================

export interface Paginated<T> {
  items: T[];
  page: number;
  per_page: number;
  total: number;
}

// ============================================================================
// Search & Query Params
// ============================================================================

export interface PlayerSearchParams {
  q?: string;
  position?: string[]; // repeatable
  team?: string[]; // repeatable NFL team abbr
  season?: number;
  week?: number;
  season_type?: "REG" | "POST";
  status?: "active" | "injured" | "all";
  available?: boolean;
  stat?: string[]; // repeatable, format: "column:op:value"
  sort?: string;
  scoring_profile_id?: number;
  page?: number;
  per_page?: number;
}

export interface PlayerCompareParams {
  ids: string[]; // comma-separated GSIS IDs, max 4
}

export interface StatsLeadersParams {
  stat: string;
  position?: string;
  week?: number;
  season?: number;
  limit?: number;
}

export interface StatsWeeksParams {
  season: number;
}
